import { Link } from "react-router-dom";
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Badge, useTheme } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMenuNavigation, useOriginalImage } from "@/hooks";
import OrderDrawer from "@/features/order/components/OrderDrawer";
import SummaryBox from "@/features/order/Order";

const MenuSidebarNavigation = () => {
  const theme = useTheme();
  const {
    organization,
    activeMenuItem,
    openOrder,
    setOpenOrder,
    menuItems,
    navigateToHome,
  } = useMenuNavigation();

  const logoUrl = useOriginalImage(organization?.logo, organization?.id);

  return (
    <Box
      sx={{
        width: 280,
        height: "100vh",
        position: "fixed",
        top: 0,
        left: 0,
        borderRight: `1px solid ${theme.palette.divider}`,
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        zIndex: 1100,
      }}
    >
      {/* Brand Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          justifyContent: 'center',
        }}
      >
        <img
          src={logoUrl}
          onClick={navigateToHome}
          alt={organization?.title || "Logo"}
          height={100}
          style={{
            maxWidth: "100%",
          }}
        />
      </Box>

      {/* Navigation List */}
      <Box sx={{ flexGrow: 1, py: 2, px: 2, overflowY: "auto" }}>
        <List component="nav" disablePadding>
          {menuItems
            .filter((item) => item.id !== "perfil")
            .map((item) => {
              const originalIndex = menuItems.findIndex((m) => m.id === item.id);
              const isActive = activeMenuItem === originalIndex;
              const iconElement =
                item.type === "hugeicons" ? (
                  <HugeiconsIcon icon={item.icon} size={22} />
                ) : (
                  <item.icon size={22} />
                );

              const isLink = !!item.to;
              const buttonProps: any = {};
              if (isLink) {
                buttonProps.component = Link;
                buttonProps.to = item.to;
              } else {
                buttonProps.onClick = item.onClick;
              }

              return (
                <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    {...buttonProps}
                    sx={{
                      borderRadius: "12px",
                      py: 1.5,
                      px: 2,
                      backgroundColor: isActive ? theme.palette.primary.main : "transparent",
                      color: isActive ? "#ffffff" : theme.palette.text.primary,
                      "&:hover": {
                        backgroundColor: isActive
                          ? theme.palette.primary.dark
                          : theme.palette.action.hover,
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: isActive ? "#ffffff" : theme.palette.text.secondary,
                      }}
                    >
                      {item.badge ? (
                        <Badge variant="dot" color={isActive ? "secondary" : "primary"}>
                          {iconElement}
                        </Badge>
                      ) : (
                        iconElement
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: isActive ? "600" : "500",
                        fontSize: "0.95rem",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
        </List>
      </Box>

      {/* Profile Menu Item at the bottom */}
      {menuItems.some((item) => item.id === "perfil") && (
        <Box
          sx={{
            py: 2,
            px: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <List component="nav" disablePadding>
            {menuItems
              .filter((item) => item.id === "perfil")
              .map((item) => {
                const originalIndex = menuItems.findIndex((m) => m.id === item.id);
                const isActive = activeMenuItem === originalIndex;
                const iconElement =
                  item.type === "hugeicons" ? (
                    <HugeiconsIcon icon={item.icon} size={22} />
                  ) : (
                    <item.icon size={22} />
                  );

                const isLink = !!item.to;
                const buttonProps: any = {};
                if (isLink) {
                  buttonProps.component = Link;
                  buttonProps.to = item.to;
                } else {
                  buttonProps.onClick = item.onClick;
                }

                return (
                  <ListItem key={item.id} disablePadding sx={{ mb: 0 }}>
                    <ListItemButton
                      {...buttonProps}
                      sx={{
                        borderRadius: "12px",
                        py: 1.5,
                        px: 2,
                        backgroundColor: isActive ? theme.palette.primary.main : "transparent",
                        color: isActive ? "#ffffff" : theme.palette.text.primary,
                        "&:hover": {
                          backgroundColor: isActive
                            ? theme.palette.primary.dark
                            : theme.palette.action.hover,
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: isActive ? "#ffffff" : theme.palette.text.secondary,
                        }}
                      >
                        {item.badge ? (
                          <Badge variant="dot" color={isActive ? "secondary" : "primary"}>
                            {iconElement}
                          </Badge>
                        ) : (
                          iconElement
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontWeight: isActive ? "600" : "500",
                          fontSize: "0.95rem",
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
          </List>
        </Box>
      )}

      {/* Order Drawer */}
      <OrderDrawer isOpen={openOrder} onClose={() => setOpenOrder(false)} anchor="right">
        <SummaryBox onClose={() => setOpenOrder(false)} />
      </OrderDrawer>
    </Box>
  );
};

export default MenuSidebarNavigation;
export { MenuSidebarNavigation };
